import { useRef, useState, useEffect } from 'react';
import { css, jsx } from '@emotion/react';
import { Badge } from '../../display/Badge/Badge.es.js';
import { Link } from '../Link/Link.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import { TypographyPDL } from '../../../theme/Typography.es.js';
import '../../../theme/ZinniaStylesReset.es.js';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { TabsDropdown } from './TabsDropdown.es.js';

const indicatorTrackHeight = 3;
const Styles = labelStyles("Tabs", {
  root: (size) => [
    css`
      ${TypographyPDL("--zds-small-print-1", "paragraph3Semibold")}
      position: relative;
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
    `,
    size === "medium" && withModifier("medium", css`
          height: var(--zds-size-medium, 50px);
        `),
    size === "large" && withModifier("large", css`
          height: var(--zds-size-large, 70px);
        `)
  ],
  primary: ({ count }) => [
    css`
      display: grid;
      height: 100%;
      // Allow shrinking
      min-width: 0;
      max-width: 100%;
    `,
    // `minmax(auto, min-content)` behaves such that at the min value of
    // `auto` items could truncate if necessary, and at the max value of
    // `min-content` items will not render any wider than they need to,
    // ensuring that they are left-aligned rather than stretching to fill
    // any leftover space.
    withModifier(`count-${count}`, css`
        grid-template-columns: repeat(${count}, minmax(auto, min-content));
      `)
  ],
  item: ({ isSelected }) => [
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--zds-space-8, 8px);
      text-align: center;
      color: var(--zds-text-default, ${Colors.neutral800});
      text-decoration: none;
      padding: 0 var(--zds-space-16, 20px);

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        color: var(--zds-text-default, ${Colors.blue});
        font: var(--zds-small-print-3);
        outline: none;
      }

      &:not([disabled]):focus-visible {
        outline: var(--zds-border-focus, none);
      }

      &[disabled] {
        color: var(--zds-text-weakest, ${Colors.neutral500});
        cursor: not-allowed;
      }
    `,
    isSelected && withModifier("selected", css`
          color: var(--zds-text-default, ${Colors.blue});
          font: var(--zds-small-print-3);
        `)
  ],
  itemText: css`
    display: block;
    white-space: nowrap;

    /*
      Applies an invisible, bold version of the text in order to prevent a layout shift when hovering over an item.
      https://css-tricks.com/bold-on-hover-without-the-layout-shift/
    */
    &:after {
      display: block;
      content: attr(data-text);
      content: attr(data-text) / '';
      height: 0;
      visibility: hidden;
      overflow: hidden;
      user-select: none;
      pointer-events: none;
      font-weight: var(--zds-semi-bold-inter);

      @media speech {
        display: none;
      }
    }
  `,
  badge: ({ isDisabled = false, isSelected = false, type = "default" }) => [
    css`
      background-color: var(
        --zds-background-stronger,
        ${Colors.neutral700}
      ) !important;
      color: var(--zds-text-default, ${Colors.neutral100}) !important;
    `,
    isSelected && [
      css`
        background-color: var(
          --zds-ui-primary-weakest,
          ${Colors.blue600}
        ) !important;
        color: var(--zds-text-weaker, ${Colors.neutral100}) !important;
      `,
      type === "brand" && css`
          background-color: var(
            --zds-ui-brand-weaker,
            ${Colors.blue600}
          ) !important;
        `
    ],
    isDisabled && css`
        background-color: var(
          --zds-background-stronger,
          ${Colors.neutral500}
        ) !important;
        color: var(--zds-text-weaker, ${Colors.neutral100}) !important;
      `
  ],
  indicatorTrack: (shouldRenderTrack) => [
    css`
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      // This ends up being a non-integer, but it actually renders better
      // in Chrome (and still renders fine in other browsers) this way.
      border-radius: var(--zds-radius-none, ${indicatorTrackHeight / 2}px);
      /*
        Because there is no 1px size token, we:
        - co-opt zds-size-2 and subtract 1px to get a 1px height for PDL
        - use a 5px fallback and subtract 1px to get a 4px height for legacy
      */
      height: calc(var(--zds-size-2, 5px) - 1px);
    `,
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    shouldRenderTrack && withModifier("visible", css`
          background-color: ${Colors.neutral300};
        `)
  ],
  indicator: css`
    position: absolute;
    height: var(--zds-size-4, inherit);
    /*
      Again, co-opting --zds-size 2 to generate:
      - a top value of -3px for PDL (so that the indicator's bottom aligns with the bottom of the track)
      - a top value of 0px for legacy (so that the indicator appears within the track)
      */
    top: calc(var(--zds-size-2, 5px) - 5px);
    border-radius: inherit;
    background-color: var(--zds-ui-primary, ${Colors.blue});
    transition: all 250ms ease-in-out;

    &[data-type='brand'] {
      background-color: var(--zds-ui-brand, ${Colors.blue});
    }
  `
});
const TabsItem = (props) => {
  if (props.disabled) {
    return jsx(
      "button",
      { type: "button", "aria-label": props.ariaLabel, css: Styles.item(props), disabled: true, "data-zds": true },
      jsx("span", { css: Styles.itemText, "data-zds": true }, props.text),
      props.badgeText && jsx(Badge, { css: Styles.badge({
        isDisabled: true,
        isSelected: props.isSelected
      }), color: "info" }, props.badgeText)
    );
  }
  return jsx(
    Link,
    { ariaLabel: props.ariaLabel, ariaCurrent: props.isSelected ? props.ariaCurrent : void 0, color: null, css: Styles.item(props), href: props.href, onClick: props.onChoose, ref: props.forwardedRef },
    jsx("span", { css: Styles.itemText, "data-text": props.text, "data-zds": true }, props.text),
    props.badgeText && jsx(Badge, { color: "info", css: Styles.badge({ isSelected: props.isSelected, type: props.type }) }, props.badgeText)
  );
};
const Tabs = ({ items, selectedItemHref, forwardedRef = void 0, ariaLabel = void 0, ariaCurrent = "page", isItemSelected = (item, selectedItemHref2) => item.href === selectedItemHref2, onChooseItem = () => {
}, shouldRenderTrack = true, size = "large", type = "default" }) => {
  const selectedItemRef = useRef(null);
  const [indicatorStyles, setIndicatorStyles] = useState({});
  const shouldRenderIndicator = !items.every((i) => i.isExtra);
  const primaryItems = items.filter((item) => !item.isExtra);
  const primaryLinks = primaryItems.map((item) => {
    const isSelected = isItemSelected(item, selectedItemHref);
    return jsx(TabsItem, Object.assign({}, item, { ariaCurrent, forwardedRef: isSelected && shouldRenderIndicator ? selectedItemRef : null, isSelected, key: item.href, onChoose: (event) => onChooseItem(item, event), type }));
  });
  const secondaryItems = items.filter((item) => item.isExtra);
  const isSecondaryItemSelected = secondaryItems.some((item) => {
    return isItemSelected(item, selectedItemHref);
  });
  const secondaryMenu = secondaryItems.length > 0 && jsx(
    "div",
    { ref: isSecondaryItemSelected && shouldRenderIndicator ? selectedItemRef : null, "data-zds": true },
    jsx(TabsDropdown, { size, items: secondaryItems, onChooseItem, selectedItemHref })
  );
  useEffect(() => {
    const requestId = requestAnimationFrame(() => {
      setIndicatorStyles(selectedItemRef.current ? {
        left: selectedItemRef.current.offsetLeft,
        width: selectedItemRef.current.offsetWidth
      } : {});
    });
    return () => cancelAnimationFrame(requestId);
  }, [items, selectedItemHref]);
  return jsx(
    "nav",
    { "aria-label": ariaLabel, css: Styles.root(size), "data-zds": true },
    jsx("div", { css: Styles.primary({ count: primaryLinks.length }), ref: forwardedRef, "data-zds": true }, primaryLinks),
    secondaryMenu,
    jsx(
      "div",
      { css: Styles.indicatorTrack(shouldRenderTrack), "data-zds": true },
      jsx("div", { css: Styles.indicator, "data-type": type, style: indicatorStyles, "data-zds": true })
    )
  );
};

export { Tabs };
//# sourceMappingURL=Tabs.es.js.map
